// useSdsSearch.js
// Local search state machine for the SDS lookup (no store slice: results are transient).
// Search is explicit (button / Enter): every call is a paid, rate-limited 3E request.

import { useCallback, useRef, useState } from "react";
import { searchSds, sdsErrorMessage, sdsRetryAfter } from "../../../api/SdsApi";

export const CAS_RE = /^\d{2,7}-\d{2}-\d$/;
// Mirrors the 3E rule: text criteria are 3..50 characters inclusive
export const MIN_TEXT_CHARS = 3;
// The S3 SDS library has no length floor, so "wd" can find WD-40
export const LIBRARY_MIN_TEXT_CHARS = 1;
export const MAX_TEXT_CHARS = 50;
export const PAGE_SIZE = 25;

export const DEFAULT_CRITERIA = Object.freeze({
  query: "",
  manufacturerName: "",
  partNumber: "",
  match: "contains",
  advancedOpen: false,
});

const textLengthMessage = (minChars) =>
  minChars > 1
    ? `Search terms need ${minChars} to ${MAX_TEXT_CHARS} characters`
    : `Search terms need at most ${MAX_TEXT_CHARS} characters`;

const textLengthOk = (value, minChars) =>
  value.length >= minChars && value.length <= MAX_TEXT_CHARS;

// Client-side mirror of the server validation. Returns { payload } or { error }.
export const validateSdsCriteria = (criteria = {}, minChars = MIN_TEXT_CHARS) => {
  const query = (criteria.query || "").trim();
  const manufacturer = (criteria.manufacturerName || "").trim();
  const partNumber = (criteria.partNumber || "").trim();
  const match = criteria.match || "contains";
  const lengthError = { error: textLengthMessage(minChars) };

  if (!query && !manufacturer && !partNumber) {
    return { error: "Enter a product name, CAS number, manufacturer, or part number" };
  }

  const payload = { match };
  if (query) {
    if (CAS_RE.test(query)) {
      payload.cas_number = query;
      // CAS is always an exact match at 3E; only force it when no other text term needs the chosen match
      if (!manufacturer && !partNumber) payload.match = "equals";
    } else if (textLengthOk(query, minChars)) {
      payload.product_name = query;
    } else {
      return lengthError;
    }
  }
  if (manufacturer) {
    if (!textLengthOk(manufacturer, minChars)) return lengthError;
    payload.manufacturer_name = manufacturer;
  }
  if (partNumber) {
    if (!textLengthOk(partNumber, minChars)) return lengthError;
    payload.part_number = partNumber;
  }
  return { payload };
};

// Trimmed copy of the criteria a search ran with; the box may be edited afterwards without a new submit
const snapshotCriteria = (criteria = {}) => ({
  query: (criteria.query || "").trim(),
  manufacturerName: (criteria.manufacturerName || "").trim(),
  partNumber: (criteria.partNumber || "").trim(),
  match: criteria.match || "contains",
});

// minTextChars: MIN_TEXT_CHARS for 3E, LIBRARY_MIN_TEXT_CHARS for the S3 library
export const useSdsSearch = ({ minTextChars = MIN_TEXT_CHARS } = {}) => {
  const [criteria, setCriteria] = useState({ ...DEFAULT_CRITERIA });
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [moreRecords, setMoreRecords] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryAfter, setRetryAfter] = useState(null);
  // Criteria that produced the current results (null until a search completes)
  const [lastSearch, setLastSearch] = useState(null);
  // Only the latest request may update state; slower earlier responses are discarded
  const requestSeq = useRef(0);
  // Payload of the last completed search, so paging never re-reads the live box
  const lastRunRef = useRef(null);

  const run = useCallback(async (payload, snapshot, pageNumber) => {
    const seq = ++requestSeq.current;
    setStatus("loading");
    setErrorMessage(null);
    setRetryAfter(null);
    try {
      const data = await searchSds(payload, pageNumber, PAGE_SIZE);
      if (seq !== requestSeq.current) return { ok: false, stale: true };
      lastRunRef.current = { payload, snapshot };
      setLastSearch(snapshot);
      const items = Array.isArray(data?.items) ? data.items : [];
      setResults(items);
      setPage(Number(data?.page) || pageNumber);
      setMoreRecords(!!data?.more_records);
      setTotalRecords(Number(data?.total_records) || items.length);
      setStatus(items.length ? "success" : "empty");
      return { ok: true, items };
    } catch (err) {
      if (seq !== requestSeq.current) return { ok: false, stale: true };
      const message = sdsErrorMessage(err);
      setErrorMessage(message);
      setRetryAfter(sdsRetryAfter(err));
      setStatus("error");
      return { ok: false, error: message };
    }
  }, []);

  // Button / Enter / Retry: the only path that reads the live criteria
  const search = useCallback(
    (pageNumber = 1) => {
      const { error, payload } = validateSdsCriteria(criteria, minTextChars);
      if (error) {
        setErrorMessage(error);
        // A stale server error would otherwise show this message in the alert with a Retry button
        setStatus((current) => (current === "error" ? "idle" : current));
        return Promise.resolve({ ok: false, error });
      }
      return run(payload, snapshotCriteria(criteria), pageNumber);
    },
    [criteria, minTextChars, run]
  );

  const nextPage = useCallback(() => {
    const last = lastRunRef.current;
    if (moreRecords && last) return run(last.payload, last.snapshot, page + 1);
    return Promise.resolve({ ok: false });
  }, [moreRecords, page, run]);

  const prevPage = useCallback(() => {
    const last = lastRunRef.current;
    if (page > 1 && last) return run(last.payload, last.snapshot, page - 1);
    return Promise.resolve({ ok: false });
  }, [page, run]);

  const reset = useCallback(() => {
    requestSeq.current += 1;
    lastRunRef.current = null;
    setCriteria({ ...DEFAULT_CRITERIA });
    setResults([]);
    setPage(1);
    setMoreRecords(false);
    setTotalRecords(0);
    setStatus("idle");
    setErrorMessage(null);
    setRetryAfter(null);
    setLastSearch(null);
  }, []);

  return {
    criteria,
    setCriteria,
    results,
    page,
    moreRecords,
    totalRecords,
    status,
    errorMessage,
    retryAfter,
    lastSearch,
    search,
    nextPage,
    prevPage,
    reset,
  };
};
